import { type ButtonHTMLAttributes, forwardRef } from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
};

const VARIANT_STYLES: Record<Variant, React.CSSProperties> = {
  primary: {
    background: 'var(--brand-primary)',
    color: 'var(--brand-text)',
    border: '1px solid var(--brand-primary)',
  },
  secondary: {
    background: 'var(--surface-base)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-default)',
  },
  danger: {
    background: 'var(--badge-rejected-bg)',
    color: 'var(--badge-rejected-text)',
    border: '1px solid var(--badge-rejected-text)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid transparent',
  },
};

const VARIANT_HOVER: Record<Variant, React.CSSProperties> = {
  primary:   { background: 'var(--brand-hover)', borderColor: 'var(--brand-hover)' },
  secondary: { background: 'var(--surface-hover)' },
  danger:    { background: '#FCCAC5' },
  ghost:     { background: 'var(--surface-hover)', color: 'var(--text-primary)' },
};

const SIZE_STYLES: Record<Size, React.CSSProperties> = {
  sm: { fontSize: 'var(--text-xs)', padding: '5px 10px', height: 28 },
  md: { fontSize: 'var(--text-sm)', padding: '7px 14px', height: 34 },
  lg: { fontSize: 'var(--text-base)', padding: '9px 18px', height: 40 },
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, children, style, onMouseEnter, onMouseLeave, ...props },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        borderRadius: 'var(--radius-md)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'background var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast)',
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
        ...VARIANT_STYLES[variant],
        ...SIZE_STYLES[size],
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) Object.assign((e.currentTarget as HTMLElement).style, VARIANT_HOVER[variant]);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) Object.assign((e.currentTarget as HTMLElement).style, VARIANT_STYLES[variant]);
        onMouseLeave?.(e);
      }}
      {...props}
    >
      {loading && (
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden
          style={{ animation: 'spin 600ms linear infinite', flexShrink: 0 }}
        >
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20 15" />
        </svg>
      )}
      {children}
    </button>
  );
});
